import FullScreenTransition from "organisms/full-screen-transition/full-screen-transition";
import AccessDeniedPage from "pages/access-denied/access-denied-page";
import * as React from "react";
import {
    Redirect,
    Route,
    RouteComponentProps,
    useLocation,
} from "react-router-dom";
import { routes } from "routes";
import { useAuthentication } from "utilities/contexts/authentication/authentication-provider";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import {
    AuthenticationLifeCycleStatus,
    AuthenticationStrategyType,
} from "utilities/enumerations/authorization";
import RoleType from "utilities/enumerations/role-type";
import { RouteDefinition } from "utilities/interfaces/route-definition";
import { RouteUtils } from "utilities/route-utils";
import { useUrlCallback } from "utilities/routing/url-callback/use-url-callback";
import { conversationsAtomErrorsEffect } from "utilities/atoms/conversations/conversation-atom-errors.effect";
import { conversationsAtomPersistenceEffect } from "utilities/atoms/conversations/conversations-atom-persistence.effect";
import { conversationsAtomSyncEffect } from "utilities/atoms/conversations/conversations-atom-sync.effect";
import { useEffect } from "react";

/*
-------------------------------------------------------------------------
Interfaces
-------------------------------------------------------------------------
*/

interface AuthenticatedRouteProps extends RouteComponentProps<any> {
    route: RouteDefinition;
    render: (props: any) => any;
}

/*
-------------------------------------------------------------------------
Components
-------------------------------------------------------------------------
*/

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
    render,
    route,
    ...rest
}) => {
    const { globalState } = useGlobalState();
    const { authenticationLifeCycleStatus } = useAuthentication();
    const location = useLocation();
    const { setCallbackUrlParam } = useUrlCallback();
    const { login } = useAuthentication();

    const renderIfAuthenticated = (props: any): any => {
        if (
            authenticationLifeCycleStatus !==
            AuthenticationLifeCycleStatus.LoggedIn
        ) {
            const callbackLink = RouteUtils.getUrl(
                location.pathname,
                null,
                location.search,
                location.hash
            );

            setCallbackUrlParam(callbackLink);

            if (route.unathenticatedRedirectToAzureB2C) {
                login(AuthenticationStrategyType.AzureB2C);
                return <FullScreenTransition transitionText={""} />;
            }

            return (
                <Redirect
                    to={{
                        pathname: routes.userlogins.routes.new.path,
                        state: { from: props.location },
                    }}
                />
            );
        }

        let allow = true;
        if (route.requiresRoles != null) {
            // checking all role types in array to see if any of them satisfy requirements
            allow = !route.requiresRoles.every(
                (roleType: RoleType) =>
                    !globalState.currentIdentity!.isCurrentRole(roleType)
            );
        }

        if (!allow) {
            return <AccessDeniedPage />;
        }

        return render(props);
    };

    useEffect(
        function conversationsAtomEffects() {
            const conversationsAtomUnobserveFuncs: Array<() => void> = [];

            if (
                globalState.isAuthenticated() &&
                authenticationLifeCycleStatus ===
                    AuthenticationLifeCycleStatus.LoggedIn
            ) {
                conversationsAtomUnobserveFuncs.push(
                    conversationsAtomPersistenceEffect(),
                    conversationsAtomSyncEffect(),
                    conversationsAtomErrorsEffect()
                );
            }

            return () => {
                for (const unobserve of conversationsAtomUnobserveFuncs) {
                    unobserve();
                }
            };
        },
        [globalState, authenticationLifeCycleStatus]
    );

    return <Route {...rest} render={renderIfAuthenticated} />;
};

export default AuthenticatedRoute;
